import React, { useEffect, useMemo, useState } from "react";

import { useDebounceEffect } from "../useDebounceEffect";
import { elementVisibilityProviderPropTypes } from "./ElementVisibilityProvider.propTypes";
import {
  ElementVisibilityProviderProps,
  RegisteredElement,
} from "./ElementVisibilityProvider.types";

export const ElementVisibilityContext = React.createContext<
  | {
      registerElement: (element: Element, metadata?: unknown) => void;
      unregisterElement: (element: Element) => void;
    }
  | undefined
>(undefined);

/**
 * ElementVisibilityProvider provides mechanisms to register an element to be watched
 * by an intersection observer to detect when the element is visible on the viewport.
 */
export const ElementVisibilityProvider = (props: ElementVisibilityProviderProps) => {
  const { children, debounceDelay = 1000, onVisible, rememberVisible = true } = props;
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);
  const [registeredElements, setRegisteredElements] = useState<RegisteredElement[]>([]);
  const visibleElementsCount = useMemo(getVisibleElementsCount, [registeredElements]);

  useEffect(() => {
    const observerInstance = new IntersectionObserver(handleObservation, { threshold: 0.5 });

    setObserver(observerInstance);

    return () => {
      observerInstance?.disconnect();
    };
  }, []);

  useDebounceEffect(handleVisibleElementsChange, [visibleElementsCount], debounceDelay);

  return (
    <ElementVisibilityContext.Provider value={getContextValue()}>
      {children}
    </ElementVisibilityContext.Provider>
  );

  function getContextValue() {
    return {
      registerElement,
      unregisterElement,
    };
  }

  function getVisibleElementsCount() {
    return registeredElements.filter((element) => element.visible).length;
  }

  function handleObservation(entries: IntersectionObserverEntry[]) {
    setRegisteredElements((previouslyRegisteredElements) => {
      const updatedElements = [...previouslyRegisteredElements];

      for (const entry of entries) {
        const { isIntersecting, target } = entry;
        const elementIndex = updatedElements.findIndex((item) => item.element === target);

        if (elementIndex !== -1) {
          const elementVisibility =
            isIntersecting || (rememberVisible && updatedElements[elementIndex].visible);

          updatedElements[elementIndex].visible = elementVisibility;
        }
      }

      return updatedElements;
    });
  }

  function handleVisibleElementsChange() {
    if (registeredElements.length > 0) {
      onVisible?.(registeredElements);
    }
  }

  function registerElement(element: Element, elementMetadata?: unknown) {
    if (element && observer) {
      setRegisteredElements((previouslyRegisteredElements) => {
        if (previouslyRegisteredElements.some((item) => item.element === element)) {
          return previouslyRegisteredElements;
        }

        observer.observe(element);

        return [
          ...previouslyRegisteredElements,
          { element, metadata: elementMetadata, visible: false },
        ];
      });
    }
  }

  function unregisterElement(element: Element) {
    if (element && observer) {
      observer.unobserve(element);
      setRegisteredElements((previouslyRegisteredElements) =>
        previouslyRegisteredElements.filter((item) => item.element !== element || item.visible)
      );
    }
  }
};

ElementVisibilityContext.displayName = "ElementVisibilityContext";
ElementVisibilityProvider.displayName = "ElementVisibilityProvider";
ElementVisibilityProvider.propTypes = elementVisibilityProviderPropTypes;
